import {
  CurrentUserTeacherUnreadCountsQueryQuery,
  CurrentUserUnreadCountsQueryQuery,
} from "@outschool/gql-frontend-generated";
import { useQueryWithPreviousData } from "@outschool/ui-apollo";
import {
  currentUserTeacherUnreadCountsQuery,
  currentUserUnreadCountsQuery,
  useSession,
} from "@outschool/ui-auth";

import * as User from "../../shared/User";
import { useTeacherUnifiedMessaging } from "./useTeacherUnifiedMessaging";

export type CurrentUserUnreadCounts = {
  uid: string;
  unreadMessageThreadsCount?: number;
  unreadPrivateClassMessageThreadsCount?: number;
  unreadMessageThreadGroupsCount?: number;
  unreadAssignmentCommentThreadsCount?: number;
};

export function useCurrentUserUnreadCountsQuery() {
  const { isLeader } = useSession();
  const isTeacherUnifiedMessagingEnabled = useTeacherUnifiedMessaging();
  return isLeader && isTeacherUnifiedMessagingEnabled
    ? currentUserTeacherUnreadCountsQuery
    : currentUserUnreadCountsQuery;
}

export function useCurrentUserUnreadCounts(): CurrentUserUnreadCounts | null {
  const { isLoggedIn, isLeader } = useSession();
  const isTeacherUnifiedMessagingEnabled = useTeacherUnifiedMessaging();
  const useTeacherCounts = Boolean(
    isLeader && isTeacherUnifiedMessagingEnabled,
  );

  const { data: parentData } =
    useQueryWithPreviousData<CurrentUserUnreadCountsQueryQuery>(
      currentUserUnreadCountsQuery,
      {
        skip: !isLoggedIn || useTeacherCounts,
        fetchPolicy: "cache-and-network",
      },
    );
  const { data: teacherData } =
    useQueryWithPreviousData<CurrentUserTeacherUnreadCountsQueryQuery>(
      currentUserTeacherUnreadCountsQuery,
      {
        skip: !isLoggedIn || !useTeacherCounts,
        fetchPolicy: "cache-and-network",
      },
    );

  return (useTeacherCounts ? teacherData : parentData)?.currentUser ?? null;
}

export function useUnreadConversationsBadgeCount() {
  const { isLeader } = useSession();
  const isTeacherUnifiedMessagingEnabled = useTeacherUnifiedMessaging();
  const unreadUser = useCurrentUserUnreadCounts();
  return User.unreadConversationsCount(unreadUser, {
    includeAssignmentCommentThreads:
      isLeader && isTeacherUnifiedMessagingEnabled,
    useMessageThreadGroupsCount: isLeader && isTeacherUnifiedMessagingEnabled,
  });
}
